import { Box, Typography, Link, Skeleton, Accordion, AccordionSummary, AccordionDetails } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import internalReach from "assets/images/background/internal-reach.png";
import defaultImg from "assets/images/blog/1.jpg";
import { useEffect, useState } from "react";
import { useParams } from "react-router";
import { Link as RouterLink, Navigate } from "react-router-dom";
import { BookAppointmentModel } from "components";
import { Helmet } from "react-helmet-async";

const DASHBOARD_URL =
  process.env.REACT_APP_DASHBOARD_URL || "https://amrithospitals.com/amritapi";

const resolveMediaUrl = (url) => {
  if (!url) return defaultImg;
  if (/^(https?:)?\/\//i.test(url) || url.startsWith("data:")) return url;
  return `${DASHBOARD_URL}/${url.replace(/^\/+/, "")}`;
};

const resolveDoctorUrl = (url) => {
  if (!url) return "";
  if (/^https?:\/\//i.test(url)) return url;
  return url.startsWith("/") ? url : `/${url}`;
};

const BlogItem = ({ img, title }) => (
  <Box display="flex" mb={3} alignItems={{ xs: "center", md: "start" }}>
    <Box
      pr={3}
      minWidth="8.75rem"
      maxWidth="8.75rem"
      textAlign={{ xs: "center", md: "start" }}
    >
      <img
        src={resolveMediaUrl(img)}
        alt={title}
        width="100%"
        height="100%"
        style={{ objectFit: "cover", borderRadius: "0.5rem" }}
      />
    </Box>
    <Box>
      <Typography fontSize="0.85rem" fontWeight="bold" mb={2}>
        {title}
      </Typography>
    </Box>
  </Box>
);

const DetailsSkeleton = () => (
  <Box pr={{ xs: 0, lg: 5 }} width="100%">
    <Skeleton variant="rectangular" width="100%" height={360} sx={{ borderRadius: "1rem" }} />
    <Skeleton variant="text" width="75%" height={72} sx={{ my: 3 }} />
    {Array.from({ length: 6 }).map((_, index) => (
      <Skeleton key={index} variant="text" width={index % 2 ? "88%" : "100%"} height={34} />
    ))}
  </Box>
);

const DoctorCard = ({ doctor }) => (
  <Box
    display="flex"
    alignItems="center"
    p={2}
    border="1px solid #e8e8e8"
    borderRadius="12px"
    bgcolor="#fafafa"
    mb={3}
  >
    <img
      src={resolveMediaUrl(doctor.photo)}
      alt={doctor.name}
      style={{
        width: 72,
        height: 72,
        borderRadius: "50%",
        marginRight: "1rem",
        objectFit: "cover",
      }}
    />
    <Box>
      <Typography fontSize=".9rem" color="text.secondary" mb={0.5}>
        Written and Verified by:
      </Typography>
      <Typography fontWeight={700} lineHeight={1.2} mb={0.5}>
        {doctor.name}
        {doctor.experience && (
          <Typography component="span" color="text.secondary" fontWeight={500}>
            &nbsp;Exp: {doctor.experience}
          </Typography>
        )}
      </Typography>
      <Typography color="text.secondary" mb={0.5}>
        {[doctor.designation, doctor.department, doctor.qualification].filter(Boolean).join(" | ")}
      </Typography>
      {doctor.profile_url && (
        <Link
          component={RouterLink}
          to={resolveDoctorUrl(doctor.profile_url)}
          underline="hover"
          fontWeight={700}
        >
          Meet the doctor
        </Link>
      )}
    </Box>
  </Box>
);

const Details = () => {
  const { slug } = useParams();
  const [blog, setBlog] = useState(null);
  const [latestBlogs, setLatestBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [openModel, setOpenModel] = useState(false);

  const openPopup = () => setOpenModel(true);
  const closePopup = () => setOpenModel(false);

  useEffect(() => {
    setLoading(true);
    setError(null);
    setNotFound(false);

    fetch(`${DASHBOARD_URL}/api.php?action=get_blog_by_slug&slug=${encodeURIComponent(slug)}`)
      .then((response) => {
        if (response.status === 404) {
          setNotFound(true);
          return null;
        }
        if (!response.ok) throw new Error("Server error: " + response.status);
        return response.json();
      })
      .then((data) => {
        if (data?.blog) setBlog(data.blog);
      })
      .catch((err) => {
        console.error("Blog detail fetch error:", err);
        setError(true);
      })
      .finally(() => setLoading(false));
  }, [slug]);

  useEffect(() => {
    fetch(`${DASHBOARD_URL}/api.php?action=get_public_blogs&page=1&limit=8`)
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => setLatestBlogs(data?.blogs || []))
      .catch((err) => console.error("Latest blogs fetch error:", err));
  }, []);

  if (notFound) return <Navigate to="/404" replace />;

  const metaTitle = blog?.meta_title || blog?.title || "Blog";
  const metaDescription = blog?.meta_description || blog?.excerpt || "Blog post detail page";
  const heroImage = resolveMediaUrl(blog?.featured_image);
  const visibleLatestBlogs = latestBlogs.filter((item) => item.slug !== slug).slice(0, 6);
  const contentHtml = blog?.content
    ? blog.content.replace(/src=(["'])(?!https?:|data:|\/\/)(\/?[^"']+)\1/g, (_, quote, src) => `src=${quote}${resolveMediaUrl(src)}${quote}`)
    : "";

  return (
    <Box
      px={{ xs: 4, lg: 10 }}
      py={{ xs: 4, lg: 8 }}
      display="flex"
      flexDirection={{ xs: "column", lg: "row" }}
    >
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDescription} />
        {blog?.meta_keywords && <meta name="keywords" content={blog.meta_keywords} />}
        {blog?.canonical_url && <link rel="canonical" href={blog.canonical_url} />}
        {blog?.og_title && <meta property="og:title" content={blog.og_title} />}
        {blog?.og_description && <meta property="og:description" content={blog.og_description} />}
        {(blog?.og_image || blog?.featured_image) && <meta property="og:image" content={resolveMediaUrl(blog.og_image || blog.featured_image)} />}
        {blog?.schema_script && <script type="application/ld+json">{blog.schema_script}</script>}
      </Helmet>

      {loading ? (
        <DetailsSkeleton />
      ) : error ? (
        <Box pr={{ xs: 0, lg: 5 }} width="100%" py={8} textAlign="center">
          <Typography color="error" fontSize="1.2rem">
            Blog detail load nahi ho saka. Server check karein.
          </Typography>
        </Box>
      ) : (
        <Box pr={{ xs: 0, lg: 5 }} width="100%">
          <img
            src={heroImage}
            alt={blog?.title || "blog"}
            width="100%"
            style={{ borderRadius: "1rem" }}
          />
          <Typography component="h1" fontSize={{ xs: "2rem", md: "2.75rem" }} fontWeight="bold" py={4}>
            {blog?.title}
          </Typography>

          {blog?.content ? (
            <Box
              color="#777"
              fontSize="1.25rem"
              lineHeight={1.8}
              sx={{
                "& p": { marginTop: 0, marginBottom: "1.5rem" },
                "& h2, & h3, & h4": { color: "#555", marginTop: "2rem", marginBottom: "1rem" },
                "& img": { maxWidth: "100%", borderRadius: "0.75rem" },
                "& a": { color: "primary.main" },
              }}
              dangerouslySetInnerHTML={{ __html: contentHtml }}
            />
          ) : (
            <Typography color="#777" fontSize="1.4rem" mb={3}>
              {blog?.excerpt}
            </Typography>
          )}

          {blog?.doctors?.map((doctor) => (
            <DoctorCard key={doctor.id || doctor.name} doctor={doctor} />
          ))}

          {blog?.faqs?.length > 0 && (
            <Box mt={5}>
              <Typography component="h2" fontSize="2rem" fontWeight="bold" mb={2}>
                FAQs
              </Typography>
              {blog.faqs.map((faq, index) => (
                <Accordion key={`${faq.question}-${index}`} disableGutters>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography fontWeight={700}>{faq.question}</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography color="#777">{faq.answer}</Typography>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          )}
        </Box>
      )}

      <Box
        flexGrow={1}
        maxWidth={{ xs: "100%", lg: "24.5rem" }}
        minWidth={{ xs: "100%", lg: "24.5rem" }}
      >
        <Box bgcolor="#f4f4f4" px={3} pb={0.25} borderRadius="1rem" mb={3}>
          <Typography
            variant="body2"
            textTransform="uppercase"
            borderBottom={1}
            mx={-3}
            px={3}
            py={2}
            borderColor="#e8e8e8"
            fontWeight={600}
            mb={3}
          >
            Latest post
          </Typography>

          {visibleLatestBlogs.map((item) => (
            <Link
              key={item.slug}
              component={RouterLink}
              to={`/blog/${item.slug}`}
              textDecoration="none"
              color="common.black"
              sx={{ textDecoration: "none", "&:hover": { textDecoration: "none" } }}
            >
              <BlogItem img={item.featured_image} title={item.title} />
            </Link>
          ))}
        </Box>

        <Box
          order={{ xs: -1, md: 2 }}
          maxWidth={{ xs: "100%", md: "24.5rem" }}
          minWidth={{ xs: "100%", md: "24.5rem" }}
          position="relative"
        >
          <img
            src={internalReach}
            alt="internalReach"
            width="100%"
            onClick={openPopup}
            style={{ cursor: "pointer" }}
          />
          <Link
            href="tel:+919600711818"
            alignItems="center"
            sx={{
              textDecoration: "none",
              width: "100%",
              height: "30px",
              display: "block",
              position: "absolute",
              bottom: "33%",
              zIndex: 1,
            }}
            color="common.black"
          />
        </Box>
      </Box>
      {openModel && <BookAppointmentModel onClose={closePopup} />}
    </Box>
  );
};

export default Details;
